// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Phase 13 · RB-4 — pure helpers over the get_effective_access() resolver payload.
// effective access = seat ceiling ∩ facility module policy ∩ facility role.
// Absent module policy = open at the ceiling; a per-member override beats the policy.

import { ALL_NAV_ITEMS, PRIMARY_NAV } from "@/config/appNavigation";
import { isFreeTierAllowedPath } from "@/lib/freeTierAccess";
import { isComingSoonNavPath } from "@/lib/platformAdmin";
import { MEMBERSHIP_TIERS } from "@/types/dashboard";
import type { NavItem } from "@/types/navigation";

export interface EffectiveAccess {
  is_member: boolean;
  role?: "owner" | "admin" | "manager" | "member" | "viewer";
  seat_tier?: string | null;
  effective_tier?: string;
  is_blessed_admin?: boolean;
  module_policies?: Record<string, { enabled: boolean; min_role: string }>;
  module_overrides?: Record<string, boolean>;
}

const ROLE_RANK: Record<string, number> = {
  viewer: 0,
  member: 1,
  manager: 2,
  admin: 3,
  owner: 4,
};

export const tierRank = (tier: string | null | undefined): number =>
  MEMBERSHIP_TIERS[(tier ?? "free") as keyof typeof MEMBERSHIP_TIERS] ?? 0;

/** The stable module id for a nav item = its top-level PRIMARY_NAV ancestor. */
export function moduleIdFor(item: NavItem): string {
  const top = PRIMARY_NAV.find(
    (m) => item.path === m.path || item.path.startsWith(`${m.path}/`),
  );
  return top?.id ?? item.id;
}

/** Does the facility's own policy (not the tier ceiling) allow this module? */
export function moduleAllowed(access: EffectiveAccess, moduleId: string): boolean {
  if (!access.is_member) return true; // no facility context → nothing to restrict
  const override = access.module_overrides?.[moduleId];
  if (override !== undefined) return override;
  const policy = access.module_policies?.[moduleId];
  if (!policy) return true;
  if (!policy.enabled) return false;
  return ROLE_RANK[access.role ?? "viewer"] >= (ROLE_RANK[policy.min_role] ?? 0);
}

export type LockReason = "tier" | "facility" | "admin" | "comingSoon" | null;

/**
 * Why is this nav item locked for the caller — or null if it isn't?
 * "tier" → upsell path (pricing); "facility" → the blessed admin scoped it out,
 * which is NOT an upsell and must never redirect to /pricing.
 */
export function lockReasonFor(
  access: EffectiveAccess | null,
  personalTier: string | null | undefined,
  item: NavItem,
  options?: { isPlatformAdmin?: boolean },
): LockReason {
  if (item.adminOnly && !options?.isPlatformAdmin) {
    return isComingSoonNavPath(item.path) ? "comingSoon" : "admin";
  }
  if (access && !moduleAllowed(access, moduleIdFor(item))) return "facility";
  const ceiling = access?.is_member ? access.effective_tier : personalTier;
  if (tierRank(ceiling) < tierRank(item.requiredTier)) return "tier";

  // Personal free accounts: calculators only (+ account/onboarding paths).
  if (tierRank(ceiling) === 0 && !options?.isPlatformAdmin) {
    return isFreeTierAllowedPath(item.path) ? null : "tier";
  }

  return null;
}

/** Shared route lookup: dashboard, shortcuts and route guards use the same policy. */
export function navItemForPath(path: string): NavItem | undefined {
  const pathname = path.split(/[?#]/)[0];
  return [...ALL_NAV_ITEMS].filter(item => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((a,b) => b.path.length - a.path.length)[0];
}
export function lockReasonForPath(access: EffectiveAccess | null, personalTier: string | null | undefined,
  path: string, admin = false): LockReason {
  const item = navItemForPath(path);
  return item ? lockReasonFor(access, personalTier, item, { isPlatformAdmin: admin }) : null;
}
